import { useState, useEffect } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";

function UserBanner() {
  const [open, setOpen] = useState(false);
  const submenuId = "submenu-user-menu";

  const USERNAME_CACHE_KEY = "ts_username";

  const [user, setUser] = useState<any | null>(null);
  const [cachedName, setCachedName] = useState<string | null>(() => {
    try {
      return localStorage.getItem(USERNAME_CACHE_KEY);
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      const name = u ? (u.displayName ?? u.email ?? null) : null;
      if (name) {
        setCachedName(name);
        try {
          localStorage.setItem(USERNAME_CACHE_KEY, name);
        } catch (e) {
          console.error("Failed to cache username:", e);
        }
      } else {
        setCachedName(null);
        try {
          localStorage.removeItem(USERNAME_CACHE_KEY);
        } catch (e) {
          console.error("Failed to remove cached username: ", e);
        }
      }
    });
    return () => unsub();
  }, []);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // second effect subscribes and flips 'initialized' after first auth state emission
    const unsub = onAuthStateChanged(auth, (_) => {
      setInitialized(true);
    });
    return () => unsub();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const showSignedIn = Boolean(user) || (!initialized && cachedName != null);

  return (
    <div
      id="ts-nav-userbanner"
      className="h-full w-fit"
    >

      {showSignedIn ? (
        <div
          className="w-full h-full relative group flex justify-center items-center cursor-pointer duration-200 p-2"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onKeyDown={handleKeyDown}
        >
          <button
            className="flex justify-center items-center focus:outline-none"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={submenuId}
          >
            <img
              src="/icons/person-circle.svg"
              alt="User Icon"
              className="w-6 h-6 mr-2"
            />
            <span className="text-md font-medium">{cachedName ?? user?.displayName ?? user?.email}</span>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls={submenuId}
              aria-label={`${open ? 'Collapse' : 'Expand'} user menu`}
              className="ml-1 p-1 rounded text-light-text hover:bg-light-border
                         focus:outline-none focus:ring-2 focus:ring-light-main/40
                         transition-colors duration-150"
            >
              <img
                src="/icons/chevron-down.svg"
                alt=""
                aria-hidden="true"
                className={`w-3 h-3 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </button>
          </button>
          <div
            id={submenuId}
            role="menu"
            className={`absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-light-bg border text-light-text border-light-border shadow-lg transition-opacity duration-200 z-10 min-w-1/2 rounded-sm ${open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'}`}
          >
            <a 
            role="menuitem"
              href="/account/downloads"
              className="block w-full text-center px-4 py-2 text-md font-medium decoration-solid duration-200 whitespace-nowrap"
              >
                Downloads
            </a>
            <a
              role="menuitem"
              href="/account/profile"
              className="block w-full text-center px-4 py-2 text-md font-medium decoration-solid duration-200 whitespace-nowrap"
            >
              Profile
            </a>
            <button
              role="menuitem"
              onClick={() => {
                try {
                  localStorage.removeItem(USERNAME_CACHE_KEY);
                  setCachedName(null);
                } catch (e) {
                  console.error("Failed to remove cached username on sign-out:", e);
                }
                signOut(auth)
                  .then(() => {
                    window.location.href = "/";
                  })
                  .catch((err) => {
                    console.error("Sign-out error:", err);
                    window.location.href = "/";
                  });
              }}
              className="w-full text-center px-4 py-2 text-md font-medium decoration-solid duration-200 whitespace-nowrap"
            >
              Sign Out
            </button>
          </div>
        </div>
      ) : (
        initialized ? (
          <div className="h-full w-fit px-2 flex justify-start items-center">
            <span className="text-md font-normal">Not currently signed in.</span>
            <span className="text-md font-medium text-light-text-link pl-2">
               <a href="/login">Sign in?</a>
            </span>
          </div>
        ) : (
          <div className="h-full w-fit px-2" />
        )
      )}
    </div>
  );
}

export default UserBanner;
